import { useEffect, useState } from 'react'
import { fetchSportMarketsTraded, updateSportMarketsTraded } from './api'
import StringList from './StringList'

interface Props {
  sportId: number
  // Trading+ (not admin) — this section is owned by the trading team, so it
  // carries its own edit gate independent of the page-level admin edit mode.
  canEdit: boolean
}

type Mode = 'view' | 'edit'

// Editable "Markets Traded" list. Self-contained: fetches its own data on
// mount, keeps its own edit/save lifecycle, and writes through the trading-
// gated PUT. Replaces the old read-only MarketsServedPanel.
export default function MarketsTradedPanel({ sportId, canEdit }: Props) {
  const [markets, setMarkets] = useState<string[]>([])
  const [baseline, setBaseline] = useState<string[]>([])
  const [mode, setMode] = useState<Mode>('view')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  // Re-fetch whenever the selected sport changes; reset any in-flight edit.
  useEffect(() => {
    let cancelled = false
    setMode('view')
    setMsg(null)
    setLoading(true)
    fetchSportMarketsTraded(sportId)
      .then((rows) => {
        if (cancelled) return
        setMarkets(rows)
        setBaseline(rows)
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sportId])

  function cancel() {
    setMarkets(baseline)
    setMode('view')
    setMsg(null)
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      await updateSportMarketsTraded(sportId, markets)
      // Re-fetch the canonical stored order (server trims/dedupes/sorts), so
      // the view reflects exactly what landed rather than the local draft.
      const saved = await fetchSportMarketsTraded(sportId)
      setMarkets(saved)
      setBaseline(saved)
      setMode('view')
      setMsg({ kind: 'ok', text: `Saved ${saved.length} market${saved.length === 1 ? '' : 's'}.` })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  const readOnly = mode === 'view'

  return (
    <div className="ad-editor-section">
      <div className="ad-markets-traded-head">
        <h3 className="b-mono">MARKETS TRADED</h3>
        {canEdit && !loading && (
          readOnly
            ? (
                <button type="button" className="b-btn b-btn-tiny" onClick={() => setMode('edit')}>
                  EDIT
                </button>
              )
            : (
                <div className="ad-markets-traded-actions">
                  <button type="button" className="b-btn-ghost b-btn-tiny" onClick={cancel} disabled={busy}>
                    CANCEL
                  </button>
                  <button type="button" className="b-btn b-btn-primary b-btn-tiny" onClick={save} disabled={busy}>
                    {busy ? 'SAVING…' : 'SAVE'}
                  </button>
                </div>
              )
        )}
      </div>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}

      {loading
        ? <p className="b-mono-dim b-tiny">Loading…</p>
        : (
            <StringList
              label="MARKETS"
              values={markets}
              onChange={setMarkets}
              readOnly={readOnly}
              placeholder="Add a market…"
            />
          )}
    </div>
  )
}
